/**
 * Marketplace country + ad attribution helpers.
 *
 * Ads land with ?country=uae&utm_source=google&utm_campaign=... — this module
 * captures those params, persists them for the session, and stamps them on the
 * booking so operators + super admin can attribute revenue to campaigns.
 */
import { isAeDomain } from './uaeLocale';

export const MARKETPLACE_COUNTRIES = [
  { code: 'UAE', label: 'UAE', flag: '🇦🇪', currency: 'AED' },
  { code: 'USA', label: 'USA', flag: '🇺🇸', currency: 'USD' },
];

export const normalizeMarketplaceCountry = (v) => {
  if (!v) return null;
  const s = String(v).trim().toUpperCase().replace(/-/g, ' ').replace(/_/g, ' ');
  if (['USA', 'US', 'UNITED STATES', 'UNITED STATES OF AMERICA'].includes(s)) return 'USA';
  if (['UAE', 'AE', 'ARE', 'UNITED ARAB EMIRATES'].includes(s)) return 'UAE';
  return null;
};

const COUNTRY_KEY = 'bf_mkt_country';

// Precedence: URL ?country= (ads) > ?mkt_country= (experience deep-links) > saved choice > .ae domain > USA
export const getMarketplaceCountry = () => {
  try {
    const q = new URLSearchParams(window.location.search);
    const param = normalizeMarketplaceCountry(q.get('country')) || normalizeMarketplaceCountry(q.get('mkt_country'));
    if (param) {
      localStorage.setItem(COUNTRY_KEY, param);
      return param;
    }
    const stored = normalizeMarketplaceCountry(localStorage.getItem(COUNTRY_KEY));
    if (stored) return stored;
  } catch (e) { /* private mode */ }
  return isAeDomain() ? 'UAE' : 'USA';
};

export const setMarketplaceCountry = (code) => {
  try { localStorage.setItem(COUNTRY_KEY, code); } catch (e) { /* private mode */ }
};

// ── Ad attribution (UTM) — persists for the session ─────────────────────────
const ATTRIB_KEY = 'bf_mkt_attrib';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

export const captureMarketplaceAttribution = () => {
  try {
    const q = new URLSearchParams(window.location.search);
    const existing = JSON.parse(sessionStorage.getItem(ATTRIB_KEY) || '{}');
    const fresh = {};
    UTM_KEYS.forEach((k) => {
      const v = q.get(k);
      if (v) fresh[k] = v.slice(0, 200);
    });
    const ref = q.get('ref');
    if (ref) fresh.ref = ref.slice(0, 100);
    const merged = { ...existing, ...fresh };
    if (!merged.landing_page) merged.landing_page = (window.location.pathname + window.location.search).slice(0, 300);
    if (!merged.referrer && document.referrer) merged.referrer = document.referrer.slice(0, 300);
    merged.country = getMarketplaceCountry();
    sessionStorage.setItem(ATTRIB_KEY, JSON.stringify(merged));
    return merged;
  } catch (e) {
    return {};
  }
};

export const getMarketplaceAttribution = () => {
  try {
    return JSON.parse(sessionStorage.getItem(ATTRIB_KEY) || '{}');
  } catch (e) {
    return {};
  }
};

// Query string carried into /book and the embedded widget so the booking gets
// tagged bf-marketplace (BM- code) with the utm snapshot.
export const marketplaceBookingParams = () => {
  const a = getMarketplaceAttribution();
  const p = new URLSearchParams();
  p.set('bf_src', 'marketplace');
  UTM_KEYS.forEach((k) => { if (a[k]) p.set(k, a[k]); });
  if (a.ref) p.set('ref', a.ref);
  p.set('mkt_country', a.country || getMarketplaceCountry());
  return p.toString();
};
